
// --- Enums ---
export enum DisplayNameFormatType {
    DEFAULT = 0,
    COMBINED = 1,
    SEPARATED_REVERSED = 2,
};

export enum DisplayNameSuffixType {
    NONE = 0,
    SCHOOL_TITLE = 1,
    SCHOOL_SHORTNAME_WITH_TITLE_FALLBACK = 2,
    CLASS_TITLE = 3,
    CLASS_TITLE_AND_SCHOOL_TITLE = 4,
    CLASS_TITLE_AND_SCHOOL_SHORTNAME_WITH_TITLE_FALLBACK = 5,
};

export enum UserOrganisationType {
    UNDEFINED = 0,
    NO_ORGANIZATION_SYNC = 1,
    ONLY_PRIMARY = 2,
    ALL_SCHOOLS = 3,
}

export enum EmailAddressPolicyType {
    NONE = 0,
    GENERATED = 1,
    ANONYMOUS = 2,
    EMAILADRESS = 3,
    USERNAME = 4,
};

export enum PasswordPolicyType {
    NONE = 0,
    STATIC = 1,
    RANDOM = 2,
};

export enum StaffOrganizationalUnit {
    NONE = 0,
    STAFF_SWEDISH = 1,
    STAFF_SCHOOL_SWEDISH = 2,
    SCHOOL_STAFF_SWEDISH = 3,
    STAFF_ENGLISH = 4,
    STAFF_SCHOOL_ENGLISH = 5,
    SCHOOL_STAFF_ENGLISH = 6,
    STAFF_SCHOOL_TYPE_SCHOOL_SWEDISH = 7,
    STAFF_SCHOOL_TYPE_SCHOOL_ENGLISH = 8,
}

export enum SuspendedStaffOrganizationalUnit {
    NONE = 0,
    SUSPENDED_USERS_SWEDISH = 1,
    SUSPENDED_STAFF_USERS_SWEDISH = 2,
    SUSPENDED_USERS_ENGLISH = 3,
    SUSPENDED_STAFF_USERS_ENGLISH = 4,
}

export enum StudentsOrganizationalUnit {
    NONE = 0,
    STUDENTS_SCHOOL_CLASS_SWEDISH = 1,
    SCHOOL_STUDENTS_CLASS_SWEDISH = 2,
    STUDENTS_STAGE_SCHOOL_CLASS_SWEDISH = 3,
    STUDENTS_SCHOOL_CLASS_ENGLISH = 4,
    SCHOOL_STUDENTS_CLASS_ENGLISH = 5,
    STUDENTS_STAGE_SCHOOL_CLASS_ENGLISH = 6,
    STUDENTS_SCHOOL_SWEDISH = 7,
    SCHOOL_STUDENTS_SWEDISH = 8,
    STUDENTS_SCHOOL_ENGLISH = 9,
    SCHOOL_STUDENTS_ENGLISH = 10,
    STUDENT_SCHOOL_TYPE_SCHOOL_CLASS_SWEDISH = 11,
    STUDENT_SCHOOL_TYPE_SCHOOL_CLASS_ENGLISH = 12,
}

export enum SuspendedStudentsOrganizationalUnit {
    NONE = 0,
    SUSPENDED_USERS_SWEDISH = 1,
    SUSPENDED_STUDENTS_USERS_SWEDISH = 2,
    SUSPENDED_USERS_ENGLISH = 3,
    SUSPENDED_STUDENTS_USERS_ENGLISH = 4,
}

export enum CustomAttributeType {
    UNDEFINED = 0,
    SCHOOL_UNIT_CODE = 1,
    SOURCE_SYSTEM_ID = 2,
    SOCIALNUMBER = 3,
}

export enum PasswordCharacterSet {
    UPPERCASE = 1,
    LOWERCASE = 2,
    NUMBERS = 4,
    SPECIAL_CHARACTERS = 8
}


// --- Interfaces ---
export interface CoreGoogleUserExportConfiguration {
    id: string
    staffConfiguration: StaffConfiguration
    studentConfiguration: StudentConfiguration
    reportRecipients: string[]
}

export interface StaffConfiguration {
    enabled: boolean,
    deleteAfterDays: number | null
    archiveGracePeriod: number
    customAttributes: CustomAttribute[]
    displayNamePolicy: DisplayNamePolicy
    emailAddressPolicy: EmailAddressPolicy
    passwordPolicy: PasswordPolicy
    organizationalUnitPolicy: StaffOrganizationalUnitPolicy
    includeHomePhone: boolean
    includeMobilePhone: boolean
    includeWorkPhone: boolean
    includeHomeAddress: boolean
    includeUserOrganisations: UserOrganisationType
}

export interface StudentConfiguration {
    enabled: boolean,
    deleteAfterDays: number | null
    archiveGracePeriod: number
    customAttributes: CustomAttribute[]
    displayNamePolicy: DisplayNamePolicy
    emailAddressPolicy: EmailAddressPolicy
    passwordPolicy: PasswordPolicy
    organizationalUnitPolicy: StudentOrganizationalUnitPolicy
    includeHomePhone: boolean
    includeMobilePhone: boolean
    includeWorkPhone: boolean
    includeHomeAddress: boolean
    includeUserOrganisations: UserOrganisationType
}

export interface DisplayNamePolicy {
    format: DisplayNameFormatType
    suffix: DisplayNameSuffixType | null
}

export interface EmailAddressPolicy {
    type: EmailAddressPolicyType
    domainName: string | null
    includeGivenName: boolean
    givenNameCharacterCount: number | null
    includeFamilyName: boolean
    familyNameCharacterCount: number | null
    separateNamesByDots: boolean
    useSerialNumber: boolean
    serialNumberDelimiter: string | null
    randomCharacterCount: number
    randomNumberCount: number
}

export interface PasswordPolicy {
    type: PasswordPolicyType
    forcePasswordChangeOnNextLogin: boolean
    staticValue: string
    useMonthAndDayFromSocialnumber: boolean
    minLength: number
    maxLength: number
    passwordCharacterSets: number
}

export interface StaffOrganizationalUnitPolicy {
    createMissingOrganisationalUnits: boolean
    organisationalUnit: StaffOrganizationalUnit
    suspendedUsersOrganisationalUnit: SuspendedStaffOrganizationalUnit
}

export interface StudentOrganizationalUnitPolicy {
    createMissingOrganisationalUnits: boolean
    organisationalUnit: StudentsOrganizationalUnit
    suspendedUsersOrganisationalUnit: SuspendedStudentsOrganizationalUnit
}

export interface CustomAttribute {
    schemaName: string
    attributeName: string
    attributeType: CustomAttributeType
    sourceSystemName: string | null
}

// --- Static Values ---
// DisplayName
export const displayNameFormats: DisplayNameFormatType[] = [
    DisplayNameFormatType.DEFAULT,
    DisplayNameFormatType.COMBINED,
    DisplayNameFormatType.SEPARATED_REVERSED,
];

export const studentDisplayNameSuffixes: DisplayNameSuffixType[] = [
    DisplayNameSuffixType.SCHOOL_TITLE,
    DisplayNameSuffixType.SCHOOL_SHORTNAME_WITH_TITLE_FALLBACK,
    DisplayNameSuffixType.CLASS_TITLE,
    DisplayNameSuffixType.CLASS_TITLE_AND_SCHOOL_TITLE,
    DisplayNameSuffixType.CLASS_TITLE_AND_SCHOOL_SHORTNAME_WITH_TITLE_FALLBACK,
];

export const staffDisplayNameSuffix: DisplayNameSuffixType[] = [
    DisplayNameSuffixType.SCHOOL_TITLE,
    DisplayNameSuffixType.SCHOOL_SHORTNAME_WITH_TITLE_FALLBACK,
];

// UserOrganisation
export const userOrganisation: UserOrganisationType[] = [
    UserOrganisationType.NO_ORGANIZATION_SYNC,
    UserOrganisationType.ONLY_PRIMARY,
    UserOrganisationType.ALL_SCHOOLS,
];

// EmailaddressPolicy
export const emailAddressPolicyTypeOptions: EmailAddressPolicyType[] = [
    EmailAddressPolicyType.GENERATED,
    EmailAddressPolicyType.ANONYMOUS,
    EmailAddressPolicyType.EMAILADRESS,
    EmailAddressPolicyType.USERNAME,
];

// OrganizationalUnitPolicy
// Student
export const studentOrganizationalUnit: StudentsOrganizationalUnit[] = [
    StudentsOrganizationalUnit.STUDENTS_SCHOOL_CLASS_SWEDISH, 
    StudentsOrganizationalUnit.SCHOOL_STUDENTS_CLASS_SWEDISH, 
    StudentsOrganizationalUnit.STUDENTS_STAGE_SCHOOL_CLASS_SWEDISH, 
    StudentsOrganizationalUnit.STUDENTS_SCHOOL_CLASS_ENGLISH, 
    StudentsOrganizationalUnit.SCHOOL_STUDENTS_CLASS_ENGLISH, 
    StudentsOrganizationalUnit.STUDENTS_STAGE_SCHOOL_CLASS_ENGLISH, 
    StudentsOrganizationalUnit.STUDENTS_SCHOOL_SWEDISH, 
    StudentsOrganizationalUnit.SCHOOL_STUDENTS_SWEDISH, 
    StudentsOrganizationalUnit.STUDENTS_SCHOOL_ENGLISH, 
    StudentsOrganizationalUnit.SCHOOL_STUDENTS_ENGLISH, 
    StudentsOrganizationalUnit.STUDENT_SCHOOL_TYPE_SCHOOL_CLASS_SWEDISH, 
    StudentsOrganizationalUnit.STUDENT_SCHOOL_TYPE_SCHOOL_CLASS_ENGLISH, 
];

export const studentSuspendedOrganizationalUnit: SuspendedStudentsOrganizationalUnit[] = [
    SuspendedStudentsOrganizationalUnit.SUSPENDED_USERS_SWEDISH, 
    SuspendedStudentsOrganizationalUnit.SUSPENDED_STUDENTS_USERS_SWEDISH,
    SuspendedStudentsOrganizationalUnit.SUSPENDED_USERS_ENGLISH,
    SuspendedStudentsOrganizationalUnit.SUSPENDED_STUDENTS_USERS_ENGLISH, 
];

// Staff
export const staffOrganizationalUnit: StaffOrganizationalUnit[] = [
    StaffOrganizationalUnit.STAFF_SWEDISH, 
    StaffOrganizationalUnit.STAFF_SCHOOL_SWEDISH, 
    StaffOrganizationalUnit.SCHOOL_STAFF_SWEDISH, 
    StaffOrganizationalUnit.STAFF_ENGLISH,
    StaffOrganizationalUnit.STAFF_SCHOOL_ENGLISH, 
    StaffOrganizationalUnit.SCHOOL_STAFF_ENGLISH, 
    StaffOrganizationalUnit.STAFF_SCHOOL_TYPE_SCHOOL_SWEDISH, 
    StaffOrganizationalUnit.STAFF_SCHOOL_TYPE_SCHOOL_ENGLISH, 
];

export const staffSuspendedOrganizationalUnit: SuspendedStaffOrganizationalUnit[] = [
    SuspendedStaffOrganizationalUnit.SUSPENDED_USERS_SWEDISH, 
    SuspendedStaffOrganizationalUnit.SUSPENDED_STAFF_USERS_SWEDISH, 
    SuspendedStaffOrganizationalUnit.SUSPENDED_USERS_ENGLISH,
    SuspendedStaffOrganizationalUnit.SUSPENDED_STAFF_USERS_ENGLISH, 
];

// PasswordPolicy
export const passwordPolicyTypeOptions: PasswordPolicyType[] = [
    PasswordPolicyType.STATIC, 
    PasswordPolicyType.RANDOM, 
];

export const passwordCharacterSets: PasswordCharacterSet[] = [
    PasswordCharacterSet.UPPERCASE,
    PasswordCharacterSet.LOWERCASE,
    PasswordCharacterSet.NUMBERS,
    PasswordCharacterSet.SPECIAL_CHARACTERS
];

// CustomAttributes
export const customAttributeTypes: CustomAttributeType[] = [
    CustomAttributeType.UNDEFINED, 
    CustomAttributeType.SCHOOL_UNIT_CODE,
    CustomAttributeType.SOURCE_SYSTEM_ID, 
    CustomAttributeType.SOCIALNUMBER, 
];